import {
  Button,
  EmptyState,
  Form,
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  NoDataIcon,
  PageHeader,
  Select,
  TextArea,
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import { useTranslation } from 'react-i18next'
import { ButtonsContainer, PageWrapper } from '../../components'
import { Form as AntdForm, InputNumber, Tooltip } from 'antd'
import AvailabilityPreferenceTable from './components/AvailabilityPreferenceTable'
import { getRequiredRule } from '../../utils'
import { useRouter } from 'hooks/useRouter'
import useUser from 'hooks/useUser/useUser'
import {
  Availability,
  AvailabilityUpdate,
  Home,
  User,
  UserTransportation,
} from '@lmc/ts-common'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { DefaultOptionType } from 'antd/lib/select'
import { handleOnFinishFailed } from 'screens/Rostering/formComponents/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { styled } from 'styled-components'
import { InputSuffix } from 'screens/Rostering/formComponents'
import { Link } from 'react-router-dom'
import { defaultAvailability } from '../utils'
import useHomeType from 'hooks/useHomeType'

type ContractAndPreferencesForm = {
  contractType?: string
  contractedHoursPerWeek?: number
  contractNotes?: string
  canDoOvertime?: boolean
  availableTransportation?: UserTransportation[]
  availability?: Availability
  rotaPreferenceNotes?: string
}

export const EditContractAndPreferencesScreen = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const topRef = useRef<HTMLDivElement | null>(null)
  const { navigate, params } = useRouter()
  const { user, updateUser, isLoadingUpdate } = useUser({
    id: params?.id,
    includes: ['rostering'],
    onUpdateSuccess: () => navigate(-1),
  })
  const [isDirty, setIsDirty] = useState(false)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )
  const { hasRota } = useHomeType()

  const contractTypeOptions: DefaultOptionType[] =
    home.rotaSettings?.contractTypes?.map((contractType) => ({
      label: contractType.name,
      // @ts-ignore not from my MR
      value: contractType.id,
    })) || []

  const updateAvailability = (
    currentAvailability: Availability,
    updates: AvailabilityUpdate,
  ): Availability => {
    // @ts-ignore
    const updatedAvailability: AvailabilityUpdate = {}

    Object.keys(currentAvailability).forEach((day) => {
      // @ts-ignore
      updatedAvailability[day] = {}
      // @ts-ignore
      currentAvailability[day].forEach((timeslot, idx) => {
        // @ts-ignore
        updatedAvailability[day][timeslot.name] = updates[day][idx].available
      })
    })

    // @ts-ignore
    return updatedAvailability
  }

  const handleOnUpdateFinish = (values: ContractAndPreferencesForm) => {
    setIsDirty(false)

    updateUser({
      workProfile: {
        ...values,
        ...(hasRota && {
          availability: updateAvailability(
            initialValues.availability || defaultAvailability,
            // @ts-ignore
            values.availability,
          ), // only until BE is fixed
        }),
      },
    } as User)
  }

  const handleFormValuesChange = (values: ContractAndPreferencesForm) => {
    if (values) {
      setIsDirty(true)
    }
  }
  const userWithWorkProfile = user as unknown as User

  const initialValues: ContractAndPreferencesForm = {
    availableTransportation: [],
    ...userWithWorkProfile?.workProfile,
  }

  const transportationOptions = Object.entries(UserTransportation).map(
    ([key, value]) => ({
      key,
      value,
    }),
  )

  return (
    <div ref={topRef} data-testid="edit-contract-and-preferences-screen">
      <PageHeader
        title={`${t('EDIT')} ${
          hasRota ? t('CONTRACT_AND_PREFERENCES') : t('CONTRACT')
        }`}
        sticky
        data-testid="edit-contract-and-preferences-header"
      >
        <ButtonsContainer data-testid="edit-contract-and-preferences-actions">
          <Button
            onClick={() => navigate(-1)}
            data-cy="cancel-contract-btn"
            data-testid="edit-contract-and-preferences-cancel-button"
          >
            {t('CANCEL')}
          </Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={isLoadingUpdate}
            data-cy="save-contract-btn"
            data-testid="edit-contract-and-preferences-save-button"
          >
            {t('SAVE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper data-testid="edit-contract-and-preferences-content">
        <Form<ContractAndPreferencesForm>
          form={form}
          onFinish={handleOnUpdateFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-cy="edit-mode-contract-container"
          data-testid="edit-contract-and-preferences-form"
        >
          <FormCard
            title={t('CONTRACT_INFORMATION')}
            data-testid="edit-contract-and-preferences-contract-card"
          >
            <FormItem
              name="contractType"
              label={<Text>{t('CONTRACT_TYPE')}</Text>}
              rules={[getRequiredRule(t('CONTRACT_TYPE'))]}
              data-testid="edit-contract-and-preferences-contract-type-field"
            >
              <Select
                options={contractTypeOptions}
                data-testid="edit-contract-and-preferences-contract-type-select"
                notFoundContent={
                  <EmptyState
                    title={
                      <EmptyStateWrapper>
                        <Text>{t('SELECT_EMPTY_STATE_CONTRACT_TYPES')}</Text>
                      </EmptyStateWrapper>
                    }
                    description={
                      <Link
                        to="/settings/contracts-and-absences"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button variant="primary">{t('ADD')}</Button>
                      </Link>
                    }
                    icon={
                      <NoDataIconWrapper>
                        <NoDataIcon />
                      </NoDataIconWrapper>
                    }
                  />
                }
              />
            </FormItem>
            <FormItem
              name="contractedHoursPerWeek"
              label={<Text>{t('CONTRACTED_HOURS_PER_WEEK')}</Text>}
              rules={[getRequiredRule(t('CONTRACTED_HOURS_PER_WEEK'))]}
              data-testid="edit-contract-and-preferences-contracted-hours-field"
            >
              <InputNumber
                type="number"
                controls={false}
                suffix={<InputSuffix>{t('HOURS_PER_WEEK')}</InputSuffix>}
                style={{ width: '100%' }}
                min={0}
                max={168}
                data-testid="edit-contract-and-preferences-contracted-hours-input"
              />
            </FormItem>
            <StyledFormItem
              name="contractNotes"
              label={<Text>{t('CONTRACT_NOTES')}</Text>}
              data-testid="edit-contract-and-preferences-contract-notes-field"
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
                maxLength={200}
                showCount
                data-testid="edit-contract-and-preferences-contract-notes-textarea"
              />
            </StyledFormItem>
          </FormCard>
          {hasRota && (
            <FormCard
              title={t('OTHER')}
              data-testid="edit-contract-and-preferences-other-card"
            >
              <FormItem
                name="canDoOvertime"
                label={<Text>{t('OVERTIME')}</Text>}
                rules={[getRequiredRule(t('OVERTIME'))]}
                data-testid="edit-contract-and-preferences-overtime-field"
              >
                <FormRadioGroup data-testid="edit-contract-and-preferences-overtime-radio-group">
                  <FormRadio
                    value
                    text={t('WILLING_TO_WORK_OVERTIME')}
                    data-testid="edit-contract-and-preferences-overtime-yes"
                  />
                  <FormRadio
                    value={false}
                    text={t('PREFERS_NOT_TO_WORK_OVERTIME')}
                    data-testid="edit-contract-and-preferences-overtime-no"
                  />
                </FormRadioGroup>
              </FormItem>
              <FormItem
                name="availableTransportation"
                label={<Text>{t('TYPE_OF_TRANSPORT')}</Text>}
                data-testid="edit-contract-and-preferences-transportation-field"
              >
                <Select
                  mode="tags"
                  options={transportationOptions}
                  data-testid="edit-contract-and-preferences-transportation-select"
                />
              </FormItem>
              <StyledFormItem
                name="rotaPreferenceNotes"
                label={
                  <StyledLabel>
                    <Text>{t('ROTA_PREFERENCES')}</Text>
                    <Tooltip title={t('ROTA_PREFERENCES_DESC_TOOLTIP')}>
                      <QuestionMarkContainer>
                        <QuestionMarkIcon />
                      </QuestionMarkContainer>
                    </Tooltip>
                  </StyledLabel>
                }
                data-testid="edit-contract-and-preferences-rota-preferences-field"
              >
                <TextArea
                  maxLength={200}
                  autoSize={{
                    minRows: 3,
                  }}
                  showCount
                  data-testid="edit-contract-and-preferences-rota-preferences-textarea"
                />
              </StyledFormItem>
              {/* <FormItem
                  name="rotaTags"
                  label={
                    <>
                      <Text>{t('ROTA_TAGS')}</Text>
                      <MoreInfoLink helpArticleUrl="todo" />
                    </>
                  }
                >
                  <Select mode="tags" />
                </FormItem> */}
            </FormCard>
          )}
          {hasRota && (
            <FormCard
              title={t('AVAILABILITY_PREFERENCE')}
              subtitle={t('AVAILABILITY_PREFERENCE_DESC')}
              data-testid="edit-contract-and-preferences-availability-card"
            >
              <AvailabilityPreferenceTable
                editMode
                availability={
                  userWithWorkProfile?.workProfile?.availability ||
                  defaultAvailability
                }
                testId="edit-contract-and-preferences-availability"
              />
            </FormCard>
          )}
        </Form>
      </PageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('CONTRACT_PREFERENCES_NAVIGATION_MODAL_CONTENT')}
      />
    </div>
  )
}

const QuestionMarkContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`

const StyledLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledFormItem = styled(FormItem)`
  padding-bottom: 24px;
`
const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const NoDataIconWrapper = styled.div`
  scale: 0.5;
  height: 50px;
  display: flex;
  align-items: center;
`
